import type {DataUploadSkippedRow} from 'common/helpers/dataUpload'
import {UserPlus} from 'lucide-react'
import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

import type {TeacherEntryRow} from '../utils/teacherEntry'

/** What the user chose for a teachers-only file's teachers. */
export type CarriedTeachersChoice = 'only' | 'classes'

interface Props {
  show: boolean
  /** The file's teachers, as Add Teachers reads them (`carried.teachers.read`). */
  rows: readonly TeacherEntryRow[]
  /** The file's rows left out, with why (`sources[].leftOut`, else the file's report). */
  leftOut: readonly DataUploadSkippedRow[]
  /** The file names their subjects and classes: "Also choose their classes" is offered. */
  canChooseClasses: boolean
  submitting?: boolean
  onConfirm: (choice: CarriedTeachersChoice) => void
  /** Cancel / X: nothing is imported, the file stays on the card (its carried draft too). */
  onCancel: () => void
}

const teacherName = (row: TeacherEntryRow) =>
  [row.first_name, row.last_name]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')

/**
 * A Teacher Allocation file that holds only teachers (QA 555): "These teachers aren't in your
 * list", each with "found twice" / "already in your list", the rows left out, then Save teachers
 * only — or, when the file names their subjects and classes, also choose their classes next.
 */
const CarriedTeachersModal: FC<Props> = ({
  show,
  rows,
  leftOut,
  canChooseClasses,
  submitting = false,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.${id}`}, values)
  const [choice, setChoice] = useState<CarriedTeachersChoice>('only')
  // Each time it opens: Save teachers only, the safe default.
  useEffect(() => {
    if (show) setChoice('only')
  }, [show])

  const option = (value: CarriedTeachersChoice, label: string, hint?: string) => (
    <label
      className={`sheet-assign-card d-flex gap-3 mb-2${choice === value ? ' is-answered' : ''}`}
    >
      <input
        type='radio'
        className='form-check-input mt-1 flex-shrink-0'
        name='carried-teachers-choice'
        checked={choice === value}
        disabled={submitting}
        onChange={() => setChoice(value)}
      />
      <span>
        <span className='fw-semibold d-block'>{label}</span>
        {hint && <span className='text-muted fs-7'>{hint}</span>}
      </span>
    </label>
  )

  return (
    <Modal show={show} onHide={onCancel} centered size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2 fs-4'>
          <UserPlus size={20} className='text-primary' aria-hidden />
          {t('CARRIED_TEACHERS_TITLE')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted mb-3'>{t('CARRIED_TEACHERS_FOUND', {count: rows.length})}</p>
        <div className='d-flex flex-column gap-2 mb-4' style={{maxHeight: 320, overflowY: 'auto'}}>
          {rows.map((row, index) => (
            <div key={`${teacherName(row)}-${index}`} className='sheet-assign-card py-2'>
              <span className='d-flex flex-wrap align-items-center gap-2'>
                <span className='sheet-assign-name'>{teacherName(row)}</span>
                {row.employeeId?.trim() && (
                  <span className='badge badge-light fs-8 fw-semibold'>
                    {row.employeeId.trim()}
                  </span>
                )}
                {(row._foundTimes ?? 0) > 1 && (
                  <span className='badge badge-light-warning fs-8 fw-semibold'>
                    {t('CARRIED_TEACHERS_FOUND_TIMES', {count: row._foundTimes ?? 0})}
                  </span>
                )}
                {row._existingTeacherId != null && (
                  <span className='badge badge-light-success fs-8 fw-semibold'>
                    {t('CARRIED_TEACHERS_IN_LIST')}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
        {leftOut.length > 0 && (
          <details className='step4-te-file-skipped mb-4'>
            <summary>
              {intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
                {count: leftOut.length}
              )}
            </summary>
            <ul className='mb-0 mt-2'>
              {leftOut.map((row, index) => (
                <li key={`${row.label ?? ''}-${index}`} className='fs-7'>
                  {String(row.label ?? '').trim() ||
                    intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})}
                  {(row.message || row.reason) && (
                    <span className='text-muted'>{` · ${row.message || row.reason}`}</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
        {option('only', t('CARRIED_TEACHERS_SAVE_ONLY'))}
        {canChooseClasses &&
          option(
            'classes',
            t('CARRIED_TEACHERS_CHOOSE_CLASSES'),
            t('CARRIED_TEACHERS_CHOOSE_CLASSES_HINT')
          )}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' disabled={submitting} onClick={onCancel}>
          {intl.formatMessage({id: 'GENERAL.CANCEL'})}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          disabled={submitting || rows.length === 0}
          onClick={() => onConfirm(choice)}
        >
          {submitting && <span className='spinner-border spinner-border-sm me-2' aria-hidden />}
          {intl.formatMessage({id: 'GENERAL.CONFIRM'})}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default CarriedTeachersModal
